import React, { useMemo } from 'react';
import { Skeleton, Typography } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
} from 'chart.js';
import Colors from '../../../../../themes/Colors';
import { resolveColor } from '../../../../../themes/theme';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

const { Text } = Typography;

export const donutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '75%',
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true }
  },
  layout: { padding: 0 },
  elements: { arc: { borderWidth: 0 } }
};

// Función: los colores del canvas se resuelven al renderizar según el tema
export const getCommonBarOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      beginAtZero: true,
      grid: { drawBorder: false, color: resolveColor(Colors.border) },
      ticks: { font: { size: 10 }, color: resolveColor(Colors.textMuted) }
    },
    x: {
      grid: { display: false },
      ticks: { font: { size: 10 }, color: resolveColor(Colors.textMuted) }
    }
  }
});

export const ChartSkeleton = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
    <Skeleton active shape="circle" style={{ width: '120px', height: '120px' }} />
  </div>
);

export const DonutWithLegend = ({ data, loading, emptyMessage }) => {
  const centerInfo = useMemo(() => {
    if (!data || !data.datasets || !data.datasets[0].data) return null;
    const values = data.datasets[0].data;
    const labels = data.labels;
    const maxVal = Math.max(...values);
    const maxIndex = values.indexOf(maxVal);
    const total = values.reduce((a, b) => a + b, 0);
    const percentage = total > 0 ? ((maxVal / total) * 100).toFixed(0) + '%' : '0%';
    return { percentage, label: labels[maxIndex] };
  }, [data]);

  if (loading) return <ChartSkeleton />;
  if (!data)
    return (
      <div
        style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Text type="secondary">{emptyMessage}</Text>
      </div>
    );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%'
      }}
    >
      <div style={{ position: 'relative', width: '140px', height: '140px', flexShrink: 0 }}>
        <Doughnut data={data} options={donutOptions} />
        {centerInfo && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              lineHeight: 1.2
            }}
          >
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: Colors.black }}>
              {centerInfo.percentage}
            </div>
            <div
              style={{
                fontSize: '11px',
                color: Colors.textSecondary,
                maxWidth: '80px',
                margin: '0 auto',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {centerInfo.label}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          marginLeft: '20px',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '8px',
          alignContent: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: '100%'
        }}
      >
        {data.labels.map((label, index) => {
          const color = data.datasets[0].backgroundColor[index];
          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
              <span
                style={{
                  width: '12px',
                  height: '4px',
                  backgroundColor: color,
                  marginRight: '8px',
                  borderRadius: '2px',
                  flexShrink: 0
                }}
              />
              <Text
                type="secondary"
                style={{
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '90px'
                }}
                title={label}
              >
                {label}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
};
